// ============================================================
// JOB SALES MODULE
// src/JobSales.js
// Isolated import of external "Job Details" sales exports (2 formats,
// 2 different old systems) + a product-category quantity report that
// also reads real work_orders/products once they exist — mirrors the
// Temp Ledger module's pattern and does not touch work_orders,
// invoices, Recon, or Sales.
// ============================================================

import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { createClient } from "@supabase/supabase-js";
import { fuzzyMatch, parseDate } from "./HistoricalImport";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

// ── SHARED STYLES (mirrors TempLedger.js) ───────────────────────
const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  btnDanger:  {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c) => ({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r) => ({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"11px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
};

// ── AUTOCOMPLETE (mirrors TempLedger.js) ────────────────────────
function AutoComplete({ value, onChange, onSelect, items, placeholder, renderItem, getKey, getLabel }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // keyboard-highlighted row
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const { list: filtered, total: matchTotal } = matchItems(items, value, getLabel, 50);
  const act = active < filtered.length ? active : -1;
  useEffect(() => {
    if (act >= 0 && listRef.current) listRef.current.children[act]?.scrollIntoView({ block: "nearest" });
  }, [act]);
  const pick = (item) => { onSelect(item); setOpen(false); setActive(-1); };
  // Arrow keys move through the suggestions, Enter picks the highlighted
  // one, Esc/Tab close the list, so entry never needs the mouse.
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(filtered.length ? 0 : -1); }
      else if (filtered.length) setActive(a => (a + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open && filtered.length) setActive(a => (a <= 0 ? filtered.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && act >= 0 && filtered[act]) { e.preventDefault(); pick(filtered[act]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setActive(-1); }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  return (
    <div className="autocomplete-wrap" ref={ref}>
      <input value={value||""} onChange={e=>{onChange(e.target.value);setOpen(true);setActive(-1);}} onFocus={()=>setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} style={S.input}/>
      {open && filtered.length > 0 && (
        <div className="autocomplete-dropdown" ref={listRef}>
          {filtered.map((item, idx) => (
            <div key={getKey(item)} className={"autocomplete-item"+(idx===act?" active":"")} onMouseDown={()=>pick(item)}>
              {renderItem(item)}
            </div>
          ))}
          {matchTotal > filtered.length && (
            <div style={{padding:"8px 12px",fontSize:11,color:"var(--ink3)"}}>Showing first {filtered.length} of {matchTotal} — keep typing to narrow</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── HELPERS ──────────────────────────────────────────────────
function chunk(arr, size) { const out=[]; for (let i=0;i<arr.length;i+=size) out.push(arr.slice(i,i+size)); return out; }

async function fetchAllPaged(queryFactory) {
  let all=[]; let from=0; const pageSize=1000;
  while (true) {
    const { data, error } = await queryFactory().range(from, from+pageSize-1);
    if (error) throw error;
    if (!data || data.length===0) break;
    all = all.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

const normalizeName = (s) => (s||"").toLowerCase().trim().replace(/[&./-]/g," ").replace(/\s+/g," ").trim();
const normDesc = (s) => (s||"").toString().trim().toLowerCase().replace(/\s+/g," ");

async function loadActiveCustomers() {
  return fetchAllPaged(() => supabase.from("customers").select("id,name,phone").eq("is_active",true).order("name"));
}

// Reads real Date objects for date-typed cells (cellDates:true, raw:true) —
// sidesteps the SheetJS dateNF/string-format ambiguity hit repeatedly
// elsewhere in this app. Auto-detects the header row (both formats have a
// title row + blank row before the real header).
async function loadXlsxRows(file) {
  if (!window.XLSX) {
    await new Promise((res,rej) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      s.onload = res; s.onerror = rej; document.head.appendChild(s);
    });
  }
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(new Uint8Array(buf), { type:"array", cellDates:true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const allRows = window.XLSX.utils.sheet_to_json(ws, { header:1, raw:true, defval:"" });
  let headerIdx = allRows.findIndex(row => row.some(c => String(c||"").trim().toLowerCase() === "job description"));
  if (headerIdx < 0) headerIdx = 0;
  const headers = allRows[headerIdx].map(h => String(h||"").trim());
  const rows = allRows.slice(headerIdx+1).filter(r => r.some(c => c!=null && c!==""));
  return { headers, rows };
}

function excelValueToISODate(v) {
  if (v instanceof Date) {
    // Excel date serials from some source files land a few seconds short
    // of the intended midnight (float drift in whatever generated the
    // file — confirmed against a real sample: 23:59:50 the day before,
    // not a clean midnight), which SheetJS preserves exactly. These cells
    // are always date-only, so if the time-of-day is within a few minutes
    // of local midnight, snap to that boundary. Must compare against LOCAL
    // midnight, not round to a UTC hour grid — timezones like IST (+5:30)
    // sit on a half-hour offset, so UTC-hour rounding never lands on local
    // midnight and silently keeps the wrong day.
    let y=v.getFullYear(), m=v.getMonth(), d=v.getDate();
    const msIntoDay = v.getTime() - new Date(y,m,d).getTime();
    if (msIntoDay > 86400000 - 300000) {
      const next = new Date(y,m,d+1);
      y=next.getFullYear(); m=next.getMonth(); d=next.getDate();
    }
    return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  }
  return parseDate(String(v||""));
}

// Capitalizes a letter whenever it isn't preceded by another letter (matches
// Python's str.title(), which this was validated against) — "3D" stays "3D",
// "GSM" becomes "Gsm", "(STAR)" becomes "(Star)".
function titleCase(s) {
  let out = "", prevIsAlpha = false;
  for (const ch of String(s).trim()) {
    const isAlpha = /[A-Za-z]/.test(ch);
    out += isAlpha ? (prevIsAlpha ? ch.toLowerCase() : ch.toUpperCase()) : ch;
    prevIsAlpha = isAlpha;
  }
  return out;
}

// ── CATEGORY CLASSIFICATION ─────────────────────────────────────
export const CATEGORY_LABELS = { pad:"Pad", paper:"Paper / Sheets", add_ons:"Add Ons", cake:"Cake", calendar:"Calendar", print:"Print", rework:"Rework", other:"Other" };
export const CATEGORIES = Object.keys(CATEGORY_LABELS);

const PREFIX_MAP = { PAD:"pad", PAPER:"paper", ADD:"add_ons", BAG:"add_ons", CAKE:"cake", CALENDAR:"calendar", PRINT:"print", PRINTS:"print", ALL:"print", COMMERCIALPRINT:"print", REWORK:"rework", ENGRAVING:"add_ons" };
const PAPER_FINISH_WORDS = new Set(["glossy","matt","smatt","velvet","hologram","transparent","luster","metallic","lenin","3d","tearable","emboss","ntg","holo","sparkle"]);

// Rule-based fallback when a description isn't already in the persisted
// category map. `sales_register`-style descriptions ("PAD-Normal Pad-12X36",
// and real product names, which follow the same "Type-Subtype[-Size]"
// convention) get an exact prefix match; `work_order_sales`-style free text
// ("Sparkle Pad", "Glossy") gets a keyword match.
export function ruleClassify(description) {
  const desc = String(description||"").trim();
  const parts = desc.split("-");
  const firstWord = parts[0].trim().split(" ")[0].trim().toUpperCase();
  if (PREFIX_MAP[firstWord]) {
    const category = PREFIX_MAP[firstWord];
    let sub = parts.length>1 ? parts[1].trim() : desc;
    let rest = parts.slice(2);
    // "ADD ONS-BAG-<subtype>-<size>" nests an extra generic "BAG" container
    // level that the shorter "BAG-<subtype>-<size>" form (same real items)
    // doesn't have — unwrap it so both converge on the same product_key.
    // Without this, Normal Bag/Jute Bag/etc. undercount because half their
    // rows silently pool into a generic "Bag" bucket instead.
    if (category === "add_ons" && sub.toUpperCase() === "BAG" && rest.length) {
      sub = rest[0].trim();
      rest = rest.slice(1);
    }
    sub = sub.replace(/-?\d+X\d+.*$/i, "").trim().replace(/\s+/g," ");
    // Pad descriptions carry a meaningful variant/style code as their 3rd
    // segment (e.g. "PAD-R Package-E1", "PAD-Special Pad S Series-25S") —
    // keep it so the report can show E1/E2/25S etc. under each pad type.
    // A pure WxH token there (e.g. "12X36") is a size, not a variant, so
    // it's still dropped like it is for every other category.
    if (category === "pad" && rest.length) {
      const variant = rest.join("-").trim();
      if (variant && !/^\d+X\d+$/i.test(variant)) sub = `${sub} - ${variant}`;
    }
    return { category, product_key: titleCase(sub || desc) };
  }
  const low = desc.toLowerCase();
  if (/pad|package|box|suitcase/.test(low)) return { category:"pad", product_key: titleCase(desc) };
  if (/bag/.test(low)) return { category:"add_ons", product_key: titleCase(desc) };
  const words = low.match(/[a-z0-9]+/g) || [];
  if (words.some(w => PAPER_FINISH_WORDS.has(w))) return { category:"paper", product_key: titleCase(desc) };
  return { category:"other", product_key: titleCase(desc) };
}

async function loadCategoryMap() {
  const rows = await fetchAllPaged(() => supabase.from("job_sales_category_map").select("*"));
  const map = {};
  rows.forEach(r => { map[r.normalized_description] = { category:r.category, product_key:r.product_key }; });
  return map;
}

function classifyLine(description, categoryMap) {
  const n = normDesc(description);
  if (categoryMap[n]) return categoryMap[n];
  return ruleClassify(description);
}

async function persistCategoryResolution(normalizedDescription, category, productKey, matchedBy) {
  await supabase.from("job_sales_category_map").upsert({
    normalized_description: normalizedDescription, category, product_key: productKey,
    matched_by: matchedBy, updated_at: new Date().toISOString(),
  }, { onConflict: "normalized_description" });
}

// ── CUSTOMER NAME RESOLUTION (dedicated map — see plan for why this
// isn't shared with temp_ledger_customer_map) ───────────────────
async function resolveCustomerNames(rawNames, customers) {
  const uniqueRaw = [...new Set(rawNames.map(n => (n||"").trim()).filter(Boolean))];
  const norms = uniqueRaw.map(normalizeName);
  const mapByNorm = {};
  for (const c of chunk(norms,200)) {
    if (!c.length) continue;
    const { data } = await supabase.from("job_sales_customer_map").select("*").in("normalized_name", c);
    (data||[]).forEach(m => { mapByNorm[m.normalized_name] = m; });
  }
  const byNorm = {};
  const newMappings = [];
  uniqueRaw.forEach((raw,i) => {
    const norm = norms[i];
    const existing = mapByNorm[norm];
    if (existing) { byNorm[norm] = { customerId: existing.customer_id, raw }; return; }
    const match = fuzzyMatch(raw, customers);
    if (match) {
      byNorm[norm] = { customerId: match.id, raw };
      newMappings.push({ raw_name: raw, normalized_name: norm, customer_id: match.id, matched_by:"auto" });
    } else {
      byNorm[norm] = { customerId: null, raw };
    }
  });
  for (const c of chunk(newMappings,200)) {
    if (!c.length) continue;
    await supabase.from("job_sales_customer_map").upsert(c, { onConflict: "normalized_name" });
  }
  return byNorm;
}

async function persistManualCustomerResolution(norm, raw, customerId) {
  await supabase.from("job_sales_customer_map").upsert({
    raw_name: raw, normalized_name: norm, customer_id: customerId,
    matched_by: "manual", updated_at: new Date().toISOString(),
  }, { onConflict: "normalized_name" });
}

// ── PARSE JOB SALES FILE (both formats) ─────────────────────────
export async function parseJobSalesFile(file) {
  const { headers, rows } = await loadXlsxRows(file);
  const headersLow = headers.map(h => h.toLowerCase());
  const colIdx = (name) => headersLow.indexOf(name);

  let format;
  if (headersLow.includes("sl no")) format = "sales_register";
  else if (headersLow.includes("customer order number")) format = "work_order_sales";
  else throw new Error("Unrecognized file — expected a 'sales register' or 'work order sales' export (couldn't find the expected header row).");

  const orders = [];
  let cur = null;

  if (format === "sales_register") {
    const iDate=colIdx("date"), iOrderNo=colIdx("order no."), iName=colIdx("name"),
          iJobDesc=colIdx("job description"), iQty=colIdx("qty"), iRate=colIdx("rate"),
          iAmount=colIdx("amount"), iRemarks=colIdx("description");
    rows.forEach(row => {
      const orderNo = row[iOrderNo];
      if (orderNo) {
        cur = {
          orderNo: String(orderNo).trim(), orderDate: excelValueToISODate(row[iDate]),
          customerName: String(row[iName]||"").trim(), seriesType: null, customerOrderNumber: null,
          remarks: row[iRemarks]!=null && row[iRemarks]!=="" ? String(row[iRemarks]) : null,
          lines: [],
        };
        orders.push(cur);
      }
      const jobdesc = row[iJobDesc];
      if (jobdesc && cur) {
        cur.lines.push({ description:String(jobdesc).trim(), qty:parseFloat(row[iQty])||0, rate:parseFloat(row[iRate])||0, amount:parseFloat(row[iAmount])||0 });
      }
    });
  } else {
    const iDate=colIdx("date"), iName=colIdx("name"), iType=colIdx("type"), iOrderNo=colIdx("order no"),
          iCustOrdNum=colIdx("customer order number"), iJobDesc=colIdx("job description"),
          iQty=colIdx("qty"), iRate=colIdx("rate"), iAmount=colIdx("amount");
    rows.forEach(row => {
      const orderNo = row[iOrderNo];
      if (orderNo) {
        cur = {
          orderNo: String(orderNo).trim(), orderDate: excelValueToISODate(row[iDate]),
          customerName: String(row[iName]||"").trim(), seriesType: row[iType]||null,
          customerOrderNumber: row[iCustOrdNum]!=null && row[iCustOrdNum]!=="" ? String(row[iCustOrdNum]) : null,
          remarks: null, lines: [],
        };
        orders.push(cur);
      }
      if (row[iRate] === "Total") return; // per-order total row, not a line item
      const jobdesc = row[iJobDesc];
      if (jobdesc && cur) {
        cur.lines.push({ description:String(jobdesc).trim(), qty:parseFloat(row[iQty])||0, rate:parseFloat(row[iRate])||0, amount:parseFloat(row[iAmount])||0 });
      }
    });
  }

  return { format, orders: orders.filter(o => o.lines.length>0) };
}

// ── QUICK-ADD CUSTOMER (mirrors TempLedger.js) ──────────────────
function QuickAddCustomerModal({ prefillName, onClose, onCreated, toast }) {
  const [name, setName]   = useState(prefillName||"");
  const [type, setType]   = useState("counter");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast("Customer name is required.","error");
    setSaving(true);
    try {
      const { data, error } = await supabase.from("customers")
        .insert({ name:name.trim(), type, phone:phone||null, is_active:true })
        .select().single();
      if (error) throw error;
      toast("Customer created.");
      onCreated(data);
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{...S.overlay, zIndex:400}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={S.pageTitle}>New customer</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"16px 24px 24px"}}>
          <label style={{...S.label,display:"block",marginBottom:5}}>Customer name *</label>
          <input value={name} onChange={e=>setName(e.target.value)} autoFocus style={{...S.input,marginBottom:14}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Type</label>
              <select value={type} onChange={e=>setType(e.target.value)} style={S.input}>
                <option value="counter">Counter</option>
                <option value="outstation">Outstation</option>
                <option value="tpf">TPF</option>
                <option value="prv">PRV</option>
              </select>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Phone</label>
              <input value={phone} onChange={e=>setPhone(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"💾 Create & use"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── UNRESOLVED CUSTOMERS PANEL (mirrors TempLedger.js) ──────────
function UnresolvedCustomersPanel({ groups, customers, onResolved, onCustomerCreated, toast }) {
  const [pickerFor, setPickerFor] = useState(null);
  const [query, setQuery] = useState({});
  const [addFor, setAddFor] = useState(null);
  if (groups.length===0) return null;
  return (
    <div style={{marginBottom:16,padding:"14px 16px",background:"var(--warn-l)",borderRadius:8}}>
      <div style={{fontSize:13,fontWeight:500,color:"var(--warn)",marginBottom:8}}>⚠ {groups.length} unresolved customer name{groups.length>1?"s":""} — resolve here or leave out (safe to re-upload later once added)</div>
      {groups.map(g => (
        <div key={g.norm} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderTop:"1px solid rgba(0,0,0,0.06)"}}>
          <div style={{flex:1,fontSize:13}}>{g.name} <span style={{color:"var(--ink3)"}}>({g.count} row{g.count>1?"s":""})</span></div>
          {pickerFor===g.norm ? (
            <div style={{width:260}}>
              <AutoComplete value={query[g.norm]||""} onChange={v=>setQuery(q=>({...q,[g.norm]:v}))}
                onSelect={c=>{ onResolved(g.norm, c); setPickerFor(null); }}
                items={customers} placeholder="Search customer…" getKey={c=>c.id} getLabel={c=>c.name}
                renderItem={c=><div>{c.name}</div>}/>
            </div>
          ) : (
            <>
              <button style={S.btnSm} onClick={()=>setPickerFor(g.norm)}>Match existing</button>
              <button style={S.btnSm} onClick={()=>setAddFor(g)}>+ Add customer</button>
            </>
          )}
        </div>
      ))}
      {addFor && (
        <QuickAddCustomerModal prefillName={addFor.name} toast={toast}
          onClose={()=>setAddFor(null)}
          onCreated={cust => { onCustomerCreated(cust); onResolved(addFor.norm, cust); setAddFor(null); }}/>
      )}
    </div>
  );
}

// ── UNRESOLVED CATEGORIES PANEL ──────────────────────────────────
function UnresolvedCategoriesPanel({ groups, onResolved }) {
  const [picks, setPicks] = useState({});
  if (groups.length===0) return null;
  return (
    <div style={{marginBottom:16,padding:"14px 16px",background:"var(--info-l)",borderRadius:8}}>
      <div style={{fontSize:13,fontWeight:500,color:"var(--info)",marginBottom:8}}>
        ℹ {groups.length} product description{groups.length>1?"s":""} not yet categorized — will import as "Other" unless resolved (remembered for future uploads once set)
      </div>
      {groups.map(g => (
        <div key={g.norm} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderTop:"1px solid rgba(0,0,0,0.06)"}}>
          <div style={{flex:1,fontSize:13}}>{g.description} <span style={{color:"var(--ink3)"}}>({g.count} row{g.count>1?"s":""})</span></div>
          <select style={{...S.input,width:180}} value={picks[g.norm]||""} onChange={e=>{
            const val = e.target.value; setPicks(p=>({...p,[g.norm]:val}));
            if (val) onResolved(g.norm, g.description, val);
          }}>
            <option value="">— Pick category —</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

// ── UPLOAD MODAL ──────────────────────────────────────────────
function JobSalesUploadModal({ onClose, onDone, toast }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [format, setFormat] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerMap, setCustomerMap] = useState({}); // norm -> {customerId, raw}
  const [categoryMap, setCategoryMap] = useState({}); // normDesc -> {category, product_key}
  const [rawOrders, setRawOrders] = useState([]); // parsed orders with resolved customer/category attached
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const attachResolution = useCallback((orders, custMap, catMap) => {
    return orders.map(o => {
      const norm = normalizeName(o.customerName);
      const cust = custMap[norm];
      const lines = o.lines.map(l => ({ ...l, ...classifyLine(l.description, catMap) }));
      const total = lines.reduce((s,l)=>s+l.amount,0);
      return { ...o, customerId: cust?cust.customerId:null, customerNorm: norm, lines, total };
    });
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setFileName(file.name);
    try {
      const { format: fmtType, orders } = await parseJobSalesFile(file);
      if (orders.length===0) { toast("No orders found in this file.","error"); setBusy(false); return; }
      const [custs, catMap] = await Promise.all([loadActiveCustomers(), loadCategoryMap()]);
      setCustomers(custs);
      const custMap = await resolveCustomerNames(orders.map(o=>o.customerName), custs);

      // dedup check against already-imported orders for this source
      const orderNos = orders.map(o=>o.orderNo);
      const existing = [];
      for (const c of chunk(orderNos,200)) {
        const { data } = await supabase.from("job_sales_orders").select("id,order_no,total_amount").eq("source",fmtType).in("order_no",c);
        existing.push(...(data||[]));
      }
      const existingByOrderNo = {};
      existing.forEach(e => { existingByOrderNo[e.order_no] = e; });

      const resolved = attachResolution(orders, custMap, catMap).map(o => {
        const ex = existingByOrderNo[o.orderNo];
        let status = "ready";
        if (ex) status = Math.abs(ex.total_amount - o.total) < 0.5 ? "duplicate" : "conflict";
        return { ...o, status, existingOrderId: ex?ex.id:null, importAnyway:false };
      });

      setFormat(fmtType);
      setCustomerMap(custMap);
      setCategoryMap(catMap);
      setRawOrders(resolved);
      setStep(2);
    } catch(e) {
      toast("Failed to read file: "+e.message,"error");
    } finally { setBusy(false); }
  };

  const resolveCustomer = (norm, cust) => {
    const nextMap = { ...customerMap, [norm]: { customerId: cust.id, raw: customerMap[norm]?.raw||norm } };
    setCustomerMap(nextMap);
    persistManualCustomerResolution(norm, nextMap[norm].raw, cust.id);
    setRawOrders(prev => prev.map(o => o.customerNorm===norm ? { ...o, customerId: cust.id } : o));
  };

  const resolveCategory = (normD, description, category) => {
    const productKey = titleCase(description);
    const nextMap = { ...categoryMap, [normD]: { category, product_key: productKey } };
    setCategoryMap(nextMap);
    persistCategoryResolution(normD, category, productKey, "manual");
    setRawOrders(prev => prev.map(o => ({
      ...o,
      lines: o.lines.map(l => normDesc(l.description)===normD ? { ...l, category, product_key: productKey } : l),
    })));
  };

  const toggleImportAnyway = (orderNo) => {
    setRawOrders(prev => prev.map(o => o.orderNo===orderNo ? { ...o, importAnyway: !o.importAnyway } : o));
  };

  const unresolvedCustomerGroups = (() => {
    const map = new Map();
    rawOrders.forEach(o => {
      if (o.customerId) return;
      if (!map.has(o.customerNorm)) map.set(o.customerNorm, { norm:o.customerNorm, name:o.customerName, count:0 });
      map.get(o.customerNorm).count++;
    });
    return [...map.values()];
  })();

  const unresolvedCategoryGroups = (() => {
    const map = new Map();
    rawOrders.forEach(o => o.lines.forEach(l => {
      if (l.category !== "other") return;
      const n = normDesc(l.description);
      if (categoryMap[n]) return; // already manually resolved this session
      if (!map.has(n)) map.set(n, { norm:n, description:l.description, count:0 });
      map.get(n).count++;
    }));
    return [...map.values()];
  })();

  const readyOrders      = rawOrders.filter(o => o.status==="ready" && o.customerId);
  const duplicateOrders  = rawOrders.filter(o => o.status==="duplicate");
  const conflictOrders   = rawOrders.filter(o => o.status==="conflict");
  const conflictImporting= conflictOrders.filter(o => o.importAnyway && o.customerId);
  const unmatchedCount   = rawOrders.filter(o => !o.customerId).length;
  const toImport = [...readyOrders, ...conflictImporting];

  const doImport = async () => {
    setBusy(true);
    try {
      let inserted = 0;
      for (const batch of chunk(toImport, 150)) {
        const orderPayload = batch.map(o => ({
          source: format, order_no: o.orderNo, customer_order_number: o.customerOrderNumber,
          order_date: o.orderDate, customer_id: o.customerId, customer_name_raw: o.customerName,
          series_type: o.seriesType, remarks: o.remarks, total_amount: o.total,
        }));
        const { data: insertedOrders, error } = await supabase.from("job_sales_orders").insert(orderPayload).select("id,order_no");
        if (error) throw error;
        const idByOrderNo = {};
        insertedOrders.forEach(r => { idByOrderNo[r.order_no] = r.id; });
        const linePayload = [];
        batch.forEach(o => {
          o.lines.forEach((l,i) => linePayload.push({
            order_id: idByOrderNo[o.orderNo], description_raw: l.description, category: l.category,
            product_key: l.product_key, quantity: l.qty, rate: l.rate, amount: l.amount, sort_order: i,
          }));
        });
        for (const lc of chunk(linePayload, 400)) {
          const { error: lerr } = await supabase.from("job_sales_lines").insert(lc);
          if (lerr) throw lerr;
        }
        inserted += batch.length;
      }
      // conflicts marked "import anyway": replace lines on the existing order
      for (const o of conflictImporting) {
        await supabase.from("job_sales_lines").delete().eq("order_id", o.existingOrderId);
        await supabase.from("job_sales_orders").update({
          customer_id: o.customerId, customer_name_raw: o.customerName, total_amount: o.total,
          remarks: o.remarks, updated_at: new Date().toISOString(),
        }).eq("id", o.existingOrderId);
        const linePayload = o.lines.map((l,i) => ({
          order_id: o.existingOrderId, description_raw: l.description, category: l.category,
          product_key: l.product_key, quantity: l.qty, rate: l.rate, amount: l.amount, sort_order: i,
        }));
        await supabase.from("job_sales_lines").insert(linePayload);
      }
      setResult({ inserted, duplicates: duplicateOrders.length, skippedConflicts: conflictOrders.length-conflictImporting.length, unresolved: unmatchedCount });
      setStep(3);
      onDone && onDone();
    } catch(e) { toast("Import failed: "+e.message,"error"); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:980,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Upload job sales</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{["","Upload file","Preview & resolve","Done"][step]}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {step===1 && (
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Accepts either "Partywise Job Details" export — the sales-register format (<i>Date, Order No., Name, Job Description, Qty, Rate, Amount, Sl No</i>) or the work-order-sales format (<i>S.No, Date, Name, Type, Order No, Customer Order Number, Job Description, Qty, Rate, Amount</i>). Detected automatically. Duplicate order numbers are skipped; a changed order is flagged for review, not silently overwritten.
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>{busy?"Reading…":"Click to select file"}</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
              </div>
            </div>
          )}
          {step===2 && (
            <div>
              <div style={{fontSize:12,color:"var(--ink3)",marginBottom:12}}>{fileName} · format: {format==="sales_register"?"Sales register":"Work order sales"}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Ready",value:readyOrders.length,color:"var(--accent)"},
                  {label:"Already exists",value:duplicateOrders.length,color:"var(--ink3)"},
                  {label:"Conflicts",value:conflictOrders.length,color:"var(--danger)"},
                  {label:"Unresolved customers",value:unresolvedCustomerGroups.length,color:"var(--warn)"},
                  {label:"Unresolved categories",value:unresolvedCategoryGroups.length,color:"var(--info)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <UnresolvedCustomersPanel groups={unresolvedCustomerGroups} customers={customers}
                onResolved={resolveCustomer} onCustomerCreated={c=>setCustomers(cs=>[...cs,c])} toast={toast}/>
              <UnresolvedCategoriesPanel groups={unresolvedCategoryGroups} onResolved={resolveCategory}/>

              <div style={{maxHeight:360,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th><th style={S.th()}>Order No.</th><th style={S.th()}>Customer</th>
                      <th style={S.th(true)}>Lines</th><th style={S.th(true)}>Total</th><th style={S.th()}>Status</th><th style={S.th()}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawOrders.slice(0,200).map((o,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                        <td style={S.td}>{fmtDMY(o.orderDate)}</td>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{o.orderNo}</td>
                        <td style={{...S.td,fontSize:12}}>
                          {o.customerId ? o.customerName : <span style={{color:"var(--warn)"}}>{o.customerName} (resolve above)</span>}
                        </td>
                        <td style={{...S.td,textAlign:"right"}}>{o.lines.length}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(o.total)}</td>
                        <td style={S.td}>
                          <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,
                            background:o.status==="ready"?"var(--accent-l)":o.status==="duplicate"?"var(--paper2)":"var(--danger-l)",
                            color:o.status==="ready"?"var(--accent)":o.status==="duplicate"?"var(--ink3)":"var(--danger)"}}>
                            {o.status==="ready"?"✓ Ready":o.status==="duplicate"?"Already exists":"⚠ Conflict"}
                          </span>
                        </td>
                        <td style={S.td}>
                          {o.status==="conflict" && (
                            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                              <input type="checkbox" checked={!!o.importAnyway} onChange={()=>toggleImportAnyway(o.orderNo)}/>
                              Import anyway
                            </label>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rawOrders.length>200 && (
                  <div style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:"var(--ink3)",borderTop:"1px solid var(--paper2)"}}>
                    Showing first 200 of {rawOrders.length} orders — all {toImport.length} ready/confirmed orders will be uploaded.
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Re-upload</button>
                <button style={S.btnPrimary} onClick={doImport} disabled={busy||toImport.length===0}>
                  {busy?"Uploading…":`⬆ Import ${toImport.length} orders`}
                </button>
              </div>
            </div>
          )}
          {step===3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20,maxWidth:520,margin:"0 auto 20px"}}>
                {[
                  {label:"Imported",value:result.inserted,color:"var(--accent)"},
                  {label:"Already existed",value:result.duplicates,color:"var(--ink3)"},
                  {label:"Skipped conflicts",value:result.skippedConflicts,color:"var(--danger)"},
                  {label:"Unresolved",value:result.unresolved,color:"var(--warn)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                <button style={S.btnPrimary} onClick={onClose}>✓ Done</button>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>Upload another</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── REPORT TAB ────────────────────────────────────────────────
function ProductReportTab({ toast }) {
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0,10));
  const [toDate, setToDate]     = useState(new Date().toISOString().slice(0,10));
  const [loading, setLoading]   = useState(false);
  const [rows, setRows]         = useState([]); // {category, product_key, qty, amount}
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Source 1: imported job sales lines
      const jsOrders = await fetchAllPaged(() => supabase.from("job_sales_orders")
        .select("id,order_date").gte("order_date",fromDate).lte("order_date",toDate));
      const orderIds = jsOrders.map(o=>o.id);
      let jsLines = [];
      for (const c of chunk(orderIds,200)) {
        if (!c.length) continue;
        const { data } = await supabase.from("job_sales_lines").select("category,product_key,quantity,amount").in("order_id",c);
        jsLines = jsLines.concat(data||[]);
      }

      // Source 2: real work orders — classify products.name with the same rule
      const wos = await fetchAllPaged(() => supabase.from("work_orders")
        .select("id").eq("is_deleted",false).neq("status","cancelled").gte("order_date",fromDate).lte("order_date",toDate));
      const woIds = wos.map(w=>w.id);
      let woLines = [];
      for (const c of chunk(woIds,200)) {
        if (!c.length) continue;
        const { data } = await supabase.from("wo_line_items").select("quantity,unit_price,products(name)").in("work_order_id",c);
        woLines = woLines.concat(data||[]);
      }

      const agg = {}; // `${category}|${product_key}` -> {category,product_key,qty,amount}
      const add = (category, productKey, qty, amount) => {
        const key = `${category}|${productKey}`;
        if (!agg[key]) agg[key] = { category, product_key: productKey, qty:0, amount:0 };
        agg[key].qty += qty; agg[key].amount += amount;
      };
      jsLines.forEach(l => add(l.category, l.product_key, parseFloat(l.quantity)||0, parseFloat(l.amount)||0));
      woLines.forEach(l => {
        const name = l.products?.name || "Unknown";
        const { category, product_key } = ruleClassify(name);
        const qty = parseFloat(l.quantity)||0;
        add(category, product_key, qty, qty*(parseFloat(l.unit_price)||0));
      });

      setRows(Object.values(agg));
    } catch(e) { toast("Failed to load report: "+e.message,"error"); }
    finally { setLoading(false); }
  }, [fromDate, toDate, toast]);

  useEffect(()=>{ load(); }, [load]);

  const byCategory = {};
  rows.forEach(r => {
    if (!byCategory[r.category]) byCategory[r.category] = { qty:0, amount:0, items:[] };
    byCategory[r.category].qty += r.qty;
    byCategory[r.category].amount += r.amount;
    byCategory[r.category].items.push(r);
  });

  const downloadCSV = () => {
    const headers = ["Category","Product","Quantity","Amount"];
    const lines = [];
    CATEGORIES.forEach(cat => {
      const g = byCategory[cat];
      if (!g) return;
      g.items.sort((a,b)=>b.qty-a.qty).forEach(item => {
        lines.push([CATEGORY_LABELS[cat], item.product_key, item.qty.toFixed(2), item.amount.toFixed(2)]);
      });
    });
    const csv = [headers,...lines].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href=url; link.download=`job-sales-report-${fromDate}-to-${toDate}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{display:"flex",gap:12,alignItems:"flex-end",marginBottom:20,flexWrap:"wrap"}}>
        <div>
          <label style={S.label}>From date</label>
          <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={S.input}/>
        </div>
        <div>
          <label style={S.label}>To date</label>
          <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={S.input}/>
        </div>
        <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
      </div>

      {loading && <div style={{textAlign:"center",padding:40,color:"var(--ink3)"}}>Loading…</div>}

      {!loading && (
        <div style={S.card}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                <th style={S.th()}>Category</th><th style={S.th(true)}>Quantity</th><th style={S.th(true)}>Amount</th><th style={S.th()}></th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.filter(c=>byCategory[c]).length===0 && (
                <tr><td colSpan={4} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>No data for this date range.</td></tr>
              )}
              {CATEGORIES.filter(c=>byCategory[c]).map(cat => {
                const g = byCategory[cat];
                const isOpen = expanded[cat] !== false;
                return (
                  <Fragment key={cat}>
                    <tr style={{borderBottom:"1px solid var(--paper2)",cursor:"pointer",background:isOpen?"var(--paper)":"#fff"}}
                      onClick={()=>setExpanded(e=>({...e,[cat]:!isOpen}))}>
                      <td style={{...S.td,fontWeight:500}}>{isOpen?"▾":"▸"} {CATEGORY_LABELS[cat]}</td>
                      <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{g.qty.toLocaleString("en-IN")}</td>
                      <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(g.amount)}</td>
                      <td style={S.td}></td>
                    </tr>
                    {isOpen && g.items.sort((a,b)=>b.qty-a.qty).map((item,i) => (
                      <tr key={cat+i} style={{borderBottom:"1px solid var(--paper2)",background:"var(--paper)"}}>
                        <td style={{...S.td,paddingLeft:36,fontSize:12,color:"var(--ink2)"}}>{item.product_key}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{item.qty.toLocaleString("en-IN")}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(item.amount)}</td>
                        <td style={S.td}></td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── PAGE ────────────────────────────────────────────────────────
export function JobSalesPage({ toast }) {
  const [tab, setTab] = useState("report");
  const [uploadModal, setUploadModal] = useState(false);

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Job Sales</div>
          <div style={S.subtitle}>External job-sales imports · product-category report</div>
        </div>
        <button style={S.btnPrimary} onClick={()=>setUploadModal(true)}>⬆ Upload job sales</button>
      </div>

      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid var(--paper3)"}}>
        {[{key:"report",label:"Product report"}].map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{padding:"10px 20px",fontSize:13,cursor:"pointer",border:"none",background:"transparent",fontFamily:"var(--mono)",
              borderBottom:`2px solid ${tab===t.key?"var(--accent)":"transparent"}`,
              color:tab===t.key?"var(--accent)":"var(--ink3)",fontWeight:tab===t.key?500:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==="report" && <ProductReportTab toast={toast}/>}

      {uploadModal && <JobSalesUploadModal onClose={()=>setUploadModal(false)} onDone={()=>{}} toast={toast}/>}
    </div>
  );
}
